import { useAnalytics } from '@ecosystem/newron-client-lib';
import { SortingOptionsType } from '@components/design/migrated/Table';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';
import {
  RelativePeriod,
  RelativePeriodLabel2,
  RelativePeriodMapping2,
} from '@components/comparison-period-selector/Types';
import {
  getCountryFromSideFilters,
  getProductGroupFromSideFilters,
  segmentsFilterWithExcludeFilterKeys,
} from '@utils/FilterUtils';
import { SortDirectionMapping } from '@utils/hook/sorting/UsePersistedSorting';
import { FilterKeys, Option, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import {
  concatenateAllSelectedSegmentFeatureName,
  concatenateSelectedFeatureName,
  doAmplitudeAnalyticsTracking,
} from '../PageAnalytics';
import {
  AnalyticTrackingBasePropertiesObj,
  ElementType,
  TrackingGeneralProp,
} from './AnalyticTrackingBasePropertiesObj';
import { IRootState } from '@state/reducers/Reducers';
import { formatFocusPeriodForTracking } from '@utils/PeriodUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';

//Example of tracking for - Page Element Viewed
/**
 analytics.track("Page Element Viewed", {
 "access": "Retail Insights"
 "locationId": "Germany" //if single select or {"Germany", "UK", "Australia"}
 "dataAvailability": "Full", //Optional if you have a trigger for this
 "elementName": "Performance",
 "elementType": "Performance Overview",
 "pageName": "Retailer Overview",
 "productGroup": "PTV" //if single select or "{PTV, Smartphones}"
 "section": "Retail Intelligence",
 "subsection": "Retailer Overview"
 "periodicity": "Weekly", //comes from Time Picker
 "retailerDataFor": "Dixons Carphone",
 "globalFilterOptionsApplied":  {"XX, YY"}, //To Be Confirmed
 "elementKPI": "Units", //KPI selected on the chart that is viewed, array if more than one KPI on load
 })
 **/

export const PAGE_ELEMENT_VIEW_TRACKING_TITLE = 'Page Element Viewed';

export enum DataAvailability {
  FULL = 'Full',
  EMPTY = 'No results available',
}

//Extend base properties
export interface PageElementViewedTracking extends AnalyticTrackingBasePropertiesObj {
  dataAvailability: string;
  elementName: string;
  elementType: string;
  chartType?: string;
  elementKpi?: string;
  elementSort?: string;
  periodicity?: string;
  focusPeriod?: string;
  comparisonPeriod?: RelativePeriodLabel2;
  compareIn?: string;
  globalFilterOptionsApplied?: string;
}

export interface PageElementViewTrackingParam {
  eventTitle: string;
  sideFilters: any;
  retailerClientState: any;
  track: (event: string, properties?: Object) => void;
  pageType: PageType;
  pageName: PageName;
  componentName: string;
  elementType: ElementType;
  subSection?: SubSection;
  chartType?: ElementType;
  elementKpi?: string;
  elementSort?: SortingOptionsType;
  mainFilterState: any;
  showPeriodicity?: boolean;
  hasData?: boolean;
  access: Object;

  //This param is added to avoid the issue of locationId being undefined due to race condition of sideFilters not being loaded yet
  //Can be removed once the issue is fixed
  initialLocationId?: Option[];
}

export const pageElementViewTrackingFunction = (param: PageElementViewTrackingParam) => {
  const {
    sideFilters,
    pageName,
    retailerClientState,
    pageType,
    componentName,
    track,
    eventTitle,
    elementType,
    chartType,
    elementKpi,
    subSection,
    elementSort,
    mainFilterState,
    showPeriodicity = true,
    hasData = true,
    access,
    initialLocationId,
  } = param;

  const selectedSideFilter = sideFilters[pageType];

  const appliedProductGroup = getProductGroupFromSideFilters(selectedSideFilter);
  const appliedFiltersCountry = getCountryFromSideFilters(selectedSideFilter);

  const filteredSegments = segmentsFilterWithExcludeFilterKeys(selectedSideFilter, [
    FilterKeys.COUNTRY,
    FilterKeys.PRODUCT_GROUP,
  ]);

  const globalFilterOptionsApplied = concatenateAllSelectedSegmentFeatureName(filteredSegments);

  const pageElementViewProperties: PageElementViewedTracking = {
    access: access,
    locationId: appliedFiltersCountry?.options.map((options) => options.label)
      ? appliedFiltersCountry?.options.map((options) => options.label)
      : initialLocationId?.map((options) => options.label),
    itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    pageName: pageName,
    retailerDataFor: retailerClientState.clientName,
    section: TrackingGeneralProp.SECTION_TRACKING_PROP,
    subsection: subSection ?? pageName,
    dataAvailability: hasData ? DataAvailability.FULL : DataAvailability.EMPTY,
    elementName: componentName,
    elementType: elementType,
    chartType: chartType,
    elementKpi: elementKpi,
    ...(elementSort && {
      elementSort: `${elementSort?.label} ${SortDirectionMapping[elementSort?.sortDir]}`,
    }),
    ...(globalFilterOptionsApplied && { globalFilterOptionsApplied }),
    ...(showPeriodicity && { periodicity: mainFilterState.timeFrame }),
    focusPeriod: formatFocusPeriodForTracking(mainFilterState.timeFrame, mainFilterState.period?.label),
    comparisonPeriod:
      RelativePeriodMapping2[mainFilterState.comparisonPeriod as RelativePeriod] ??
      (mainFilterState.comparisonPeriod as BaseTimePeriod).label,
    compareIn: capitalize(mainFilterState.selectedGrowthType),
  };

  doAmplitudeAnalyticsTracking(eventTitle, track, pageElementViewProperties);
};

/**
 * Subset data type for PageElementViewTrackingParam, returning all generic value
 */
type PageElementViewTrackingParamSubset = Pick<
  PageElementViewTrackingParam,
  'eventTitle' | 'sideFilters' | 'retailerClientState' | 'track' | 'mainFilterState'
>;

/**
 * custom hook to generate pageElementViewParam common value for Product Analyzer page
 */
export const useGeneratePageElementViewParam: (
  mainFilters?: 'mainFilters' | 'mainFiltersRO' | 'mainFiltersProducts' | 'mainFiltersMarketPremium',
) => PageElementViewTrackingParamSubset = (mainFilters = 'mainFiltersProducts') => {
  // Parameters for Amplitude Tracking
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const mainFiltersState = useSelector((state: IRootState) => state[mainFilters]);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);

  return {
    eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
    sideFilters: sideFiltersState.appliedFilters,
    retailerClientState: retailerClientState,
    track: track,
    mainFilterState: mainFiltersState,
  };
};
